import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import axiosInstance from 'config/axiosInstance';

const LiveWebinars = ({ onEnrollClick }) => {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWebinars();
  }, []);

  const fetchWebinars = async () => {
    try {
      setLoading(true);
      const webinarsResponse = await axiosInstance.get('/webinars/upcoming/');
      const webinarsData = webinarsResponse.data.results || webinarsResponse.data;
      const transformedWebinars = Array.isArray(webinarsData)
        ? webinarsData.map(transformWebinarData).slice(0, 6)
        : [];
      setWebinars(transformedWebinars);
      setError(null);
    } catch (err) {
      console.error('Error fetching webinars:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load webinars');
      setWebinars([]);
    } finally {
      setLoading(false);
    }
  };

  const transformWebinarData = (apiWebinar) => {
    const utcDate = new Date(apiWebinar.scheduled_date);
    const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const month = months[istDate.getUTCMonth()];
    const day = istDate.getUTCDate();
    const year = istDate.getUTCFullYear();

    const format12h = (date) => {
      let hours = date.getUTCHours();
      const minutes = date.getUTCMinutes().toString().padStart(2,'0');
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${period}`;
    };

    const pstTime = format12h(istDate) + ' PST';
    const estTime = format12h(new Date(istDate.getTime() + 3*60*60*1000)) + ' EST';
    const timeString = `${pstTime} | ${estTime}`;

    return {
      id: apiWebinar.id,
      webinarId: apiWebinar.webinar_id,
      title: apiWebinar.title,
      date: day.toString(),
      month: month.toUpperCase(),
      instructor: {
        name: apiWebinar.speaker?.user?.full_name || apiWebinar.speaker?.name || 'Instructor',
        avatar: apiWebinar.speaker?.user?.avatar || apiWebinar.speaker?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face&auto=format&q=60'
      },
      image: apiWebinar.cover_image_url || 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=500&h=300&fit=crop&auto=format&q=80',
      rating: apiWebinar.analytics?.average_rating || 4.8,
      reviewCount: apiWebinar.analytics?.total_reviews ? `${(apiWebinar.analytics.total_reviews / 1000).toFixed(1)}k` : '0',
      price: `$${parseFloat(apiWebinar.main_price).toFixed(2)}`,
      originalPrice: apiWebinar.original_price ? `$${parseFloat(apiWebinar.original_price).toFixed(2)}` : null,
      duration: apiWebinar.duration || 'N/A',
      level: apiWebinar.level || 'All Levels',
      students: apiWebinar.total_students ? `${(apiWebinar.total_students / 1000).toFixed(1)}k` : '0',
      bestseller: apiWebinar.is_bestseller || false,
      category: apiWebinar.category?.name || 'General',
      tags: apiWebinar.tags || ['Live Session'],
      fullDate: `${month} ${day}, ${year}`,
      timeRange: timeString
    };
  };

  if (loading) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-96">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-red-600" />
            </div>
            <p className="text-red-800 font-bold text-lg mb-2">Unable to load webinars</p>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchWebinars}
              className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-lg mb-6 border-2 border-blue-100">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-3 h-3 bg-red-500 rounded-full"
            />
            <span className="text-sm font-bold text-gray-900">LIVE SESSIONS AVAILABLE</span>
          </div>

          <h2 className="text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
              Upcoming Webinars
            </span>
          </h2>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Transform your career with expert-led interactive sessions
          </p>

          <button
            onClick={() => onEnrollClick && onEnrollClick(null)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-10 py-4 rounded-full hover:shadow-2xl transition-all"
          >
            Explore All Sessions
          </button>
        </motion.div>

        {/* Cards Grid */}
        {webinars.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {webinars.map((webinar, idx) => (
              <motion.div
                key={webinar.id}
                initial={{ opacity: 0, y: 50, rotate: -2 }}
                whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ 
                  y: -12, 
                  rotate: 2,
                  transition: { type: "spring", stiffness: 300 }
                }}
                onClick={() => onEnrollClick && onEnrollClick(webinar)}
                className="relative cursor-pointer group"
              >
                {/* Neon Glow Border */}
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600 rounded-3xl blur-lg opacity-25 group-hover:opacity-75 transition-all duration-500"></div>
                
                {/* Main Card */}
                <div className="relative bg-white rounded-3xl overflow-hidden shadow-xl">
                  {/* Image Section */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={webinar.image}
                      alt={webinar.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
                    
                    {/* Live Badge - Top Left */}
                    <motion.div 
                      className="absolute top-4 left-4"
                      animate={{ 
                        boxShadow: [
                          '0 0 10px rgba(239, 68, 68, 0.5)',
                          '0 0 20px rgba(239, 68, 68, 0.8)',
                          '0 0 10px rgba(239, 68, 68, 0.5)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <div className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-full text-xs font-bold border-2 border-white">
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        LIVE
                      </div>
                    </motion.div>

                    {/* Circular Date Badge - OVER Image (Top Right) */}
                    <motion.div 
                      className="absolute top-4 right-4 z-20"
                      initial={{ scale: 0, rotate: -180 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 + 0.3, type: "spring", stiffness: 200 }}
                    >
                      <div className="relative">
                        {/* Outer Gradient Ring */}
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-blue-600 p-1 shadow-2xl">
                          {/* White Circle */}
                          <div className="w-full h-full bg-white rounded-full flex flex-col items-center justify-center">
                            <div className="text-2xl font-black bg-gradient-to-br from-blue-600 to-indigo-600 bg-clip-text text-transparent leading-none">
                              {webinar.date}
                            </div>
                            <div className="text-[10px] font-bold text-indigo-600 uppercase mt-0.5">
                              {webinar.month}
                            </div>
                          </div>
                        </div>
                        {/* Animated Glow */}
                        <motion.div
                          animate={{ 
                            scale: [1, 1.2, 1],
                            opacity: [0.3, 0.6, 0.3]
                          }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 blur-xl -z-10"
                        />
                      </div>
                    </motion.div>
                  </div>

                  {/* Content Section - Normal padding */}
                  <div className="p-6">
                    {/* Category Tag */}
                    <div className="inline-block px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full text-xs font-bold text-indigo-700 mb-3">
                      {webinar.category}
                    </div>

                    <h3 className="text-xl font-black text-gray-900 mb-3 line-clamp-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-indigo-600 group-hover:bg-clip-text transition-all">
                      {webinar.title}
                    </h3>

                    {/* Instructor */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="relative">
                        <img
                          src={webinar.instructor.avatar}
                          alt={webinar.instructor.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-blue-200"
                        />
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-bold text-gray-900">{webinar.instructor.name}</div>
                        <div className="flex items-center gap-2 text-xs">
                          <Icon name="Star" size={12} className="text-yellow-400 fill-current" />
                          <span className="font-semibold text-gray-600">{webinar.rating.toFixed(1)}</span>
                          <span className="text-gray-400">({webinar.reviewCount})</span>
                        </div>
                      </div>
                    </div>

                    {/* Meta Info Bar */}
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl mb-4">
                      <div className="flex items-center gap-1 text-xs">
                        <Icon name="Clock" size={14} className="text-blue-600" />
                        <span className="font-semibold text-gray-700">{webinar.duration} min</span>
                      </div>
                      <div className="w-px h-4 bg-gray-300"></div>
                      <div className="flex items-center gap-1 text-xs">
                        <Icon name="Users" size={14} className="text-indigo-600" />
                        <span className="font-semibold text-gray-700">{webinar.students}</span>
                      </div>
                      <div className="w-px h-4 bg-gray-300"></div>
                      <div className="text-xs font-semibold text-indigo-600">{webinar.level}</div>
                    </div>

                    {/* Date & Time */}
                    <div className="bg-white border-2 border-blue-100 rounded-xl p-3 mb-4">
                      <div className="text-xs font-bold text-blue-900 mb-1">{webinar.fullDate}</div>
                      <div className="text-xs text-indigo-700 flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        {webinar.timeRange}
                      </div>
                    </div>

                    {/* Price & CTA */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-black bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                          {webinar.price}
                        </div>
                        {webinar.originalPrice && (
                          <div className="text-sm text-gray-400 line-through">{webinar.originalPrice}</div>
                        )}
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEnrollClick && onEnrollClick(webinar);
                        }}
                        className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-6 py-3 rounded-xl shadow-lg group/btn"
                      >
                        <span className="relative z-10 flex items-center gap-2">
                          Enroll Now
                          <Icon name="ArrowRight" size={16} />
                        </span>
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-600"
                          initial={{ x: '100%' }}
                          whileHover={{ x: 0 }}
                          transition={{ duration: 0.3 }}
                        />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Calendar" size={48} className="text-indigo-600" />
            </div>
            <p className="text-xl font-bold text-gray-900 mb-2">No upcoming webinars</p>
            <p className="text-gray-600">Check back soon for new sessions</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default LiveWebinars;
